import dayjs from "dayjs";
import React from 'react';
import Chip from 'components/Chip';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import InfoIconWithTooltip from "components/InfoIconWithTooltip";

import { SimpleSelect } from 'components/CustomSelectFields';
import { WhiteBox } from 'components/AppShell';
import { useServices } from 'contexts/SingleSessionProvider';
import { useSnackbar } from 'contexts/SnackbarProvider';
import { getCodeChallengeAttempts } from "services";
import { formatDate, Session } from "utils";
import { Skeleton } from "@mui/material";

const useStyles = makeStyles((theme) => ({
  headerDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: '560px'
  },
  bottomContent: {
    display: 'flex',
    gap: theme.spacing(4)
  }
}))

const Review4cHeader = ({ currentAttempt, setCurrentAttempt, details, isGuestApp }) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [attempts, setAttempts] = React.useState([]);

  const calculateTimeDifference = React.useCallback((start, end) => {
    const startTime = dayjs(start);
    const endTime = dayjs(end);

    const totalSeconds = endTime.diff(startTime, 'second');
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }, []);

  const {
    codeChallengeId, completionTime, title, tags, attemptedOn, attempted, unFinished
  } = React.useMemo(() => {
    const { codeChallenge, finishedAt, startedAt, codeSubmissions } = details || {};

    return {
      codeChallengeId: codeChallenge?._id?.toString() || "",
      completionTime: finishedAt ? calculateTimeDifference(startedAt, finishedAt) : "--",
      unFinished: !finishedAt,
      title: codeChallenge?.title || "--",
      tags: codeChallenge?.tags?.map((tag) => (tag.name.toUpperCase())) || [],
      attemptedOn: formatDate(startedAt, 'DD/MM/YYYY'),
      attempted: codeSubmissions?.filter(
        (cc) => ["main", "follow_up_1", "follow_up_2"].includes(cc.questionType)
      )?.length || 0
    }
  }, [details]);


  React.useEffect(() => {
    if (!details || isGuestApp) return;

    setLoading(true);

    const filters = {
      user: Session.userId, includeTotal: true, codeChallenges: [codeChallengeId], feedback: null
    };

    service.callService(getCodeChallengeAttempts, filters)
      .then(({ total, codeChallengeAttempts }) => {
        if (!total) throw new Error("Attempts not found!");

        setAttempts(codeChallengeAttempts.map(
          (attempt, i) => ({ ...attempt, name: `Attempt ${i + 1}` })
        ));
      })
      .catch((error) => {
        console.error(error);
        snackbar.error("Uh Oh! Unable to get attempts. Please try again.", false);
      })
      .finally(() => setLoading(false));
  }, [details, isGuestApp]);

  return (

    <WhiteBox sx={{ width: '100%' }}>
      {
        loading ? <>
          <Skeleton variant='rectangular' width={'100%'} height={120} style={{ borderRadius: 4 }} />
        </> :
          <Box display='flex' justifyContent='space-between'>
            <Box className={classes.headerDetails}>
              <Box display='flex' gap='8px' flexWrap='wrap'>
                {tags.map((name, key) => (
                  <Chip
                    content={name}
                    key={key}
                    bgColor='#E5F1FF'
                    sx={{ padding: '4px 16px', borderRadius: '4px' }}
                  />
                ))}
              </Box>
              <Typography
                variant='h5-medium'
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {title}
              </Typography>
              <Divider sx={{ borderColor: '#CCD4DE' }} />
              <Box display='flex' flexDirection='column' gap='4px'>
                <Box className={classes.bottomContent}>
                  <Box>
                    <Typography variant='body01-semiBold' color='neutral.clr-600'>
                      Attempted on :&nbsp;
                    </Typography>
                    <Typography variant='body01-bold'>{attemptedOn}</Typography>
                  </Box>
                  <Box>
                    <Typography variant='body01-semiBold' color='neutral.clr-600'>
                      Total attempts :&nbsp;
                    </Typography>
                    <Typography variant='body01-bold'>
                      {attempts.length < 10 ? `0${attempts.length}` : attempts.length}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.bottomContent}>
                  <Box>
                    <Typography variant='body01-semiBold' color='neutral.clr-600'>
                      Attempted questions :&nbsp;
                    </Typography>
                    <Typography variant='body01-bold'>
                      {`0${attempted > 3 ? 3 : attempted}`}/03
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center' justifyContent='center'>
                    <Typography variant='body01-semiBold' color='neutral.clr-600'>
                      Completion time :&nbsp;
                    </Typography>
                    <Typography variant='body01-bold'>{completionTime}&nbsp;</Typography>
                    {(unFinished) && <InfoIconWithTooltip
                      sx={{ fontSize: '16px', textAlign: 'center', marginTop: '2px' }}
                      title="This attempt ended midway, so we couldn't record the completion time!"
                    />}
                  </Box>
                </Box>
              </Box>
            </Box>
            <SimpleSelect
              label='Select attempt'
              options={attempts}
              onSelect={(data) => setCurrentAttempt(data._id)}
              value={{ _id: currentAttempt }}
              defaultValue={{ _id: currentAttempt }}
              loading={loading}
              disabled={isGuestApp}
            />
          </Box>
      }

    </WhiteBox>
  )
}

export default Review4cHeader